import React, { useEffect, useState, useRef } from "react";
import { Container, Card, CardBody, CardHeader, Row, Col, Button, Label, Input, Spinner, Alert, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import {
  fetchSettings,
  uploadLogoImage,
  uploadDarkLogoImage,
  uploadLoginDialogImageFile,
  uploadHeroSliderImages,
  removeHeroSlider,
} from "../../slices/settings/thunk";
import { fetchFarmhouses } from "../../slices/farmhouse/thunk";
import { api } from "../../config";
import { createHeroSlider, updateHeroSlider, getFarmhouseDetails, getHeroSliderById } from "../../helpers/apis";
import { showToast } from "../../slices/toast/reducer";

const Settings = () => {
  const dispatch = useDispatch();
  const { settings, loading, uploadLoading, error } = useSelector((state) => ({
    settings: state.Settings?.settings,
    loading: state.Settings?.loading,
    uploadLoading: state.Settings?.uploadLoading,
    error: state.Settings?.error,
  }));

  const [logoFile, setLogoFile] = useState(null);
  const [darkLogoFile, setDarkLogoFile] = useState(null);
  const [loginDialogFile, setLoginDialogFile] = useState(null);
  const [heroSliderFiles, setHeroSliderFiles] = useState([]);
  const [logoPreview, setLogoPreview] = useState(null);
  const [darkLogoPreview, setDarkLogoPreview] = useState(null);
  const [loginDialogPreview, setLoginDialogPreview] = useState(null);
  const [heroSliderPreviews, setHeroSliderPreviews] = useState([]);

  // Modal states
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewIsVideo, setPreviewIsVideo] = useState(false);
  const [activeUploadType, setActiveUploadType] = useState("logo"); // logo, login, hero

  const logoInputRef = useRef(null);
  const darkLogoInputRef = useRef(null);
  const loginDialogInputRef = useRef(null);
  const heroSliderInputRef = useRef(null);

  // Hero Slider Management States
  const [heroSliderModalOpen, setHeroSliderModalOpen] = useState(false);
  const [heroSliderViewModalOpen, setHeroSliderViewModalOpen] = useState(false);
  const [viewingHeroSlider, setViewingHeroSlider] = useState(null);
  const [viewingHeroSliderLoading, setViewingHeroSliderLoading] = useState(false);
  const [editingHeroSlider, setEditingHeroSlider] = useState(null);
  const [heroSliderFile, setHeroSliderFile] = useState(null);
  const [heroSliderPreview, setHeroSliderPreview] = useState(null);
  const [heroSliderIsVideo, setHeroSliderIsVideo] = useState(false);
  const [selectedFarmhouse, setSelectedFarmhouse] = useState(null);
  const [farmhouses, setFarmhouses] = useState([]);
  const [farmhousesLoading, setFarmhousesLoading] = useState(false);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [availableAmenities, setAvailableAmenities] = useState([]);
  const [amenitiesLoading, setAmenitiesLoading] = useState(false);
  const [farmPrice, setFarmPrice] = useState(null);
  const [farmNearby, setFarmNearby] = useState("");
  const [submittingHeroSlider, setSubmittingHeroSlider] = useState(false);
  const heroSliderFileInputRef = useRef(null);

  useEffect(() => {
    dispatch(fetchSettings());
    loadFarmhouses();
  }, [dispatch]);

  const loadFarmhouses = async () => {
    setFarmhousesLoading(true);
    try {
      const result = await dispatch(fetchFarmhouses({ limit: 1000 })).unwrap();
      if (result?.data?.farmhouses) {
        setFarmhouses(result.data.farmhouses);
      }
    } catch (error) {
      console.error("Error loading farmhouses:", error);
    } finally {
      setFarmhousesLoading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;

    let path = "";
    if (typeof imagePath === 'string') {
      path = imagePath;
    } else if (typeof imagePath === 'object') {
      // Direct keys
      path = imagePath.url ||
        imagePath.imagePath ||
        imagePath.path ||
        imagePath.logo ||
        imagePath.darkLogo ||
        imagePath.image ||
        imagePath.logoUrl ||
        imagePath.imageUrl ||
        imagePath.file ||
        imagePath.fileName ||
        imagePath.image_url;

      // Nested .data keys
      if (!path && imagePath.data) {
        if (typeof imagePath.data === 'string') {
          path = imagePath.data;
        } else if (typeof imagePath.data === 'object') {
          path = imagePath.data.url ||
            imagePath.data.imagePath ||
            imagePath.data.path ||
            imagePath.data.logo ||
            imagePath.data.darkLogo ||
            imagePath.data.image ||
            imagePath.data.logoUrl ||
            imagePath.data.imageUrl ||
            imagePath.data.file ||
            imagePath.data.fileName;

          // Double nested for cases like response.data.logo.url
          if (!path && imagePath.data.logo) {
            path = imagePath.data.logo.url || imagePath.data.logo.imagePath || imagePath.data.logo.path;
          }
          if (!path && imagePath.data.darkLogo) {
            path = imagePath.data.darkLogo.url || imagePath.data.darkLogo.imagePath || imagePath.data.darkLogo.path;
          }
        }
      }

      // If we still don't have a path, and there's a property that looks like a path/filename
      if (!path) {
        const potentialKeys = Object.keys(imagePath).filter(k =>
          typeof imagePath[k] === 'string' &&
          imagePath[k].length > 4 &&
          (imagePath[k].includes('.') || imagePath[k].includes('/') || imagePath[k].includes('\\'))
        );
        if (potentialKeys.length > 0) {
          path = imagePath[potentialKeys[0]];
        }
      }
    }

    if (!path || typeof path !== 'string') return null;

    // Remove any unexpected quotes if API returned them
    path = path.replace(/['"]+/g, '').trim();
    if (!path) return null;

    // If it's already a full URL, data URL, or blob URL, return as is
    if (path.startsWith("http") || path.startsWith("data:") || path.startsWith("blob:")) return path;

    // Normalize slashes and remove all leading slashes/backslashes
    const normalizedPath = path.replace(/\\/g, '/');
    const cleanPath = normalizedPath.replace(/^[/\\]+/, '');

    // Safely get API URL - check all possible config structures
    let apiUrl = "";
    try {
      // Try current config structure, prioritizing IMG_URL
      apiUrl = api?.IMG_URL || api?.API_URL || (api?.api && (api.api.IMG_URL || api.api.API_URL)) || "";

      // Fallback: check if we can get it from process.env directly
      if (!apiUrl && typeof process !== 'undefined' && process.env) {
        apiUrl = process.env.REACT_APP_IMG_URL || process.env.REACT_APP_API_URL || "";
      }
    } catch (e) {
      apiUrl = "";
    }

    if (!apiUrl) {
      return `/${cleanPath}`;
    }

    const baseUrl = apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`;
    const finalUrl = `${baseUrl}${cleanPath}`;
    return finalUrl;
  };

  // Set previews from settings
  useEffect(() => {
    if (settings) {
      // Logo - new API returns data directly
      if (settings.logo) {
        setLogoPreview(getImageUrl(settings.logo));
      }
      // Dark Logo
      if (settings.darkLogo) {
        setDarkLogoPreview(getImageUrl(settings.darkLogo));
      }
      // Login dialog image
      if (settings.image) {
        setLoginDialogPreview(getImageUrl(settings.image));
      }
      // Hero sliders - array of objects with url or imagePath property
      if (settings.sliders && Array.isArray(settings.sliders)) {
        setHeroSliderPreviews(settings.sliders.map((slider) => getImageUrl(slider)));
      }
    }
  }, [settings]);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDarkLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setDarkLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setDarkLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLoginDialogChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLoginDialogFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLoginDialogPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleHeroSliderChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setHeroSliderFiles(files);
      const readers = files.map((file) => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
      });
      Promise.all(readers).then((previews) => {
        setHeroSliderPreviews((prev) => [...prev, ...previews]);
      });
    }
  };

  const handleUploadLogo = () => {
    if (logoFile) {
      dispatch(uploadLogoImage(logoFile));
      setLogoFile(null);
      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }
    }
  };

  const handleUploadDarkLogo = () => {
    if (darkLogoFile) {
      dispatch(uploadDarkLogoImage(darkLogoFile));
      setDarkLogoFile(null);
      if (darkLogoInputRef.current) {
        darkLogoInputRef.current.value = "";
      }
    }
  };

  const handleUploadLoginDialog = () => {
    if (loginDialogFile) {
      dispatch(uploadLoginDialogImageFile(loginDialogFile));
      setLoginDialogFile(null);
      if (loginDialogInputRef.current) {
        loginDialogInputRef.current.value = "";
      }
    }
  };

  const handleUploadHeroSliders = () => {
    if (heroSliderFiles.length > 0) {
      dispatch(uploadHeroSliderImages(heroSliderFiles));
      setHeroSliderFiles([]);
      if (heroSliderInputRef.current) {
        heroSliderInputRef.current.value = "";
      }
    }
  };

  const handleDeleteHeroSlider = (sliderIdOrPath) => {
    // New API uses ID, but handle both ID and filename for backward compatibility
    if (sliderIdOrPath && window.confirm("Are you sure you want to delete this hero slider?")) {
      dispatch(removeHeroSlider(sliderIdOrPath));
    }
  };


  const extractFilename = (path) => {
    if (!path) return "";
    return path.split("/").pop();
  };

  const openPreviewModal = (imageUrl, title, isVideo = false) => {
    setPreviewImage(imageUrl);
    setPreviewTitle(title);
    setPreviewIsVideo(isVideo);
    setPreviewModalOpen(true);
  };

  const openUploadModal = (type) => {
    setActiveUploadType(type);
    setUploadModalOpen(true);
  };

  const handleFileSelect = (e, type) => {
    const files = Array.from(e.target.files);

    if (type === "logo" && files[0]) {
      const file = files[0];
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else if (type === "darkLogo" && files[0]) {
      const file = files[0];
      setDarkLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setDarkLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else if (type === "login" && files[0]) {
      const file = files[0];
      setLoginDialogFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLoginDialogPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else if (type === "hero" && files.length > 0) {
      setHeroSliderFiles(files);
      const readers = files.map((file) => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
      });
      Promise.all(readers).then((previews) => {
        setHeroSliderPreviews((prev) => [...prev, ...previews]);
      });
    }
  };

  const handleUploadFromModal = () => {
    if (activeUploadType === "logo" && logoFile) {
      handleUploadLogo();
      setUploadModalOpen(false);
    } else if (activeUploadType === "darkLogo" && darkLogoFile) {
      handleUploadDarkLogo();
      setUploadModalOpen(false);
    } else if (activeUploadType === "login" && loginDialogFile) {
      handleUploadLoginDialog();
      setUploadModalOpen(false);
    } else if (activeUploadType === "hero" && heroSliderFiles.length > 0) {
      handleUploadHeroSliders();
      setUploadModalOpen(false);
    }
  };

  // Hero Slider Management Functions
  const openHeroSliderModal = async (slider = null) => {
    if (slider) {
      // Edit mode - fetch full hero slider data from API
      try {
        const response = await getHeroSliderById(slider.id);
        if (!response.error && response.data) {
          const sliderData = response.data;
          setEditingHeroSlider(sliderData);

          // Set farmhouse selection
          const farmhouseOption = farmhouses.find(f => f.id === sliderData.farmId);
          if (farmhouseOption) {
            setSelectedFarmhouse({
              value: sliderData.farmId,
              label: `${sliderData.name}${farmhouseOption.farmNo ? ` (${farmhouseOption.farmNo})` : ''}`
            });
          } else {
            setSelectedFarmhouse({ value: sliderData.farmId, label: sliderData.name });
          }

          // Set price from API response
          if (sliderData.price) {
            setFarmPrice(parseFloat(sliderData.price));
          }

          // Set nearby from API response
          if (sliderData.nearby) {
            setFarmNearby(sliderData.nearby);
          }

          // Set preview image/video
          if (sliderData.heroImage?.url || sliderData.heroImage?.imagePath) {
            const imagePath = sliderData.heroImage.url || sliderData.heroImage.imagePath || '';
            setHeroSliderPreview(getImageUrl(imagePath));
            setHeroSliderIsVideo(imagePath.toLowerCase().match(/\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv)$/i) || false);
          }

          // Load amenities for the farmhouse and map selected amenities from API response
          await loadAmenitiesForFarm(sliderData.farmId, sliderData.amenities);
        } else {
          dispatch(showToast({ type: "error", msg: response.msg || "Failed to load hero slider details" }));
          return;
        }
      } catch (error) {
        console.error("Error fetching hero slider:", error);
        dispatch(showToast({ type: "error", msg: "Failed to load hero slider details" }));
        return;
      }
    } else {
      // Create mode - reset form
      setEditingHeroSlider(null);
      resetHeroSliderForm();
    }
    setHeroSliderModalOpen(true);
  };

  const resetHeroSliderForm = () => {
    setHeroSliderFile(null);
    setHeroSliderPreview(null);
    setHeroSliderIsVideo(false);
    setSelectedFarmhouse(null);
    setSelectedAmenities([]);
    setAvailableAmenities([]);
    setFarmPrice(null);
    setFarmNearby("");
    if (heroSliderFileInputRef.current) {
      heroSliderFileInputRef.current.value = "";
    }
  };

  const handleHeroSliderFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setHeroSliderFile(file);
      const isVideo = file.type.startsWith('video/') || file.name.toLowerCase().match(/\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv)$/i);
      setHeroSliderIsVideo(isVideo);

      if (isVideo) {
        setHeroSliderPreview(URL.createObjectURL(file));
      } else {
        const reader = new FileReader();
        reader.onloadend = () => {
          setHeroSliderPreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleCancelHeroSliderFile = () => {
    setHeroSliderFile(null);
    setHeroSliderPreview(null);
    setHeroSliderIsVideo(false);
    if (heroSliderFileInputRef.current) {
      heroSliderFileInputRef.current.value = "";
    }
  };

  const handleFarmhouseChange = async (selectedOption) => {
    setSelectedFarmhouse(selectedOption);
    if (selectedOption) {
      const farmId = selectedOption.value;
      const farmhouse = farmhouses.find(f => f.id === farmId);

      // Auto-fill price and nearby
      if (farmhouse) {
        if (farmhouse.location?.nearby) {
          setFarmNearby(farmhouse.location.nearby);
        }
        // Get 24 hour weekend price (WEEKEND_24HR) from price options
        if (farmhouse.priceOptions && farmhouse.priceOptions.length > 0) {
          const weekend24hrPrice = farmhouse.priceOptions.find(
            p => p.category === "WEEKEND_24HR"
          );
          if (weekend24hrPrice && weekend24hrPrice.price) {
            setFarmPrice(parseFloat(weekend24hrPrice.price));
          } else {
            // Fallback to minimum price if WEEKEND_24HR not found
            const prices = farmhouse.priceOptions.map(p => parseFloat(p.price)).filter(p => p > 0);
            if (prices.length > 0) {
              setFarmPrice(Math.min(...prices));
            } else {
              setFarmPrice(null);
            }
          }
        } else {
          setFarmPrice(null);
        }
      }

      // Load amenities for selected farmhouse
      await loadAmenitiesForFarm(farmId);
    } else {
      setFarmPrice(null);
      setFarmNearby("");
      setAvailableAmenities([]);
      setSelectedAmenities([]);
    }
  };

  const loadAmenitiesForFarm = async (farmId, sliderAmenities = null) => {
    setAmenitiesLoading(true);
    try {
      const response = await getFarmhouseDetails(farmId);
      if (!response.error && response.data) {
        // Extract amenities from farmhouse details
        // The amenities are typically in farmhouseAmenities array
        const farmhouseData = response.data;
        let amenities = [];

        if (farmhouseData.farmhouseAmenities && Array.isArray(farmhouseData.farmhouseAmenities)) {
          amenities = farmhouseData.farmhouseAmenities.map((fa) => ({
            id: fa.amenity?.id || fa.amenityId,
            name: fa.amenity?.name || '',
            category: fa.category?.name || fa.category || "Other",
            quantity: fa.quantity || 1
          })).filter(a => a.id && a.name);
        }

        setAvailableAmenities(amenities);

        // Update price from farmhouse details (24 hour weekend price)
        if (farmhouseData.priceOptions && Array.isArray(farmhouseData.priceOptions)) {
          const weekend24hrPrice = farmhouseData.priceOptions.find(
            p => p.category === "WEEKEND_24HR"
          );
          if (weekend24hrPrice && weekend24hrPrice.price) {
            setFarmPrice(parseFloat(weekend24hrPrice.price));
          }
        }

        // Map selected amenities from slider to available amenities (for edit mode)
        // Handle nested structure: { amenity: { id, name }, category: { id, name } }
        if (sliderAmenities && Array.isArray(sliderAmenities) && sliderAmenities.length > 0) {
          const mappedAmenities = sliderAmenities
            .map(sa => {
              // Handle nested structure from API
              const amenityId = sa.amenityId || sa.amenity?.id || sa.id;
              const amenityName = sa.amenity?.name || sa.name;
              const categoryName = sa.category?.name || sa.category || "Other";

              // Find matching amenity in available amenities
              const matched = amenities.find(a => {
                const aId = a.id || a.amenityId;
                return aId === amenityId;
              });

              if (matched) {
                return {
                  id: matched.id,
                  name: matched.name,
                  category: matched.category || categoryName
                };
              } else if (amenityId && amenityName) {
                // If not found in available amenities, use the data from slider
                return {
                  id: amenityId,
                  name: amenityName,
                  category: categoryName
                };
              }
              return null;
            })
            .filter(a => a !== null);

          if (mappedAmenities.length > 0) {
            setSelectedAmenities(mappedAmenities);
          }
        }
      }
    } catch (error) {
      console.error("Error loading amenities:", error);
      dispatch(showToast({ type: "error", msg: "Failed to load amenities" }));
    } finally {
      setAmenitiesLoading(false);
    }
  };

  const toggleAmenity = (amenity) => {
    if (!amenity) return;

    setSelectedAmenities(prev => {
      // Handle different amenity structures
      const amenityId = amenity.id || amenity.amenityId;
      const amenityName = amenity.name || amenity.amenity?.name || 'Unknown';
      const amenityCategory = amenity.category || amenity.category?.name || "Other";

      if (!amenityId) return prev;

      const isSelected = prev.some(a => (a.id || a.amenityId) === amenityId);
      if (isSelected) {
        return prev.filter(a => (a.id || a.amenityId) !== amenityId);
      } else {
        if (prev.length >= 4) {
          dispatch(showToast({ type: "warning", msg: "Maximum 4 amenities allowed" }));
          return prev;
        }
        return [...prev, {
          id: amenityId,
          name: amenityName,
          category: amenityCategory
        }];
      }
    });
  };

  const handleSubmitHeroSlider = async () => {
    if (!selectedFarmhouse) {
      dispatch(showToast({ type: "error", msg: "Please select a farmhouse" }));
      return;
    }

    if (selectedAmenities.length < 1) {
      dispatch(showToast({ type: "error", msg: "Please select at least 1 amenity" }));
      return;
    }

    if (selectedAmenities.length > 4) {
      dispatch(showToast({ type: "error", msg: "Maximum 4 amenities allowed" }));
      return;
    }

    if (!editingHeroSlider && !heroSliderFile) {
      dispatch(showToast({ type: "error", msg: "Please upload an image or video" }));
      return;
    }

    setSubmittingHeroSlider(true);
    try {
      const formData = new FormData();
      formData.append('farmId', selectedFarmhouse.value.toString());
      formData.append('amenities', JSON.stringify(selectedAmenities.map(a => a.id)));

      if (heroSliderFile) {
        formData.append('file', heroSliderFile);
      }

      let response;
      if (editingHeroSlider) {
        response = await updateHeroSlider(editingHeroSlider.id, formData);
      } else {
        response = await createHeroSlider(formData);
      }

      if (!response.error) {
        dispatch(showToast({ type: "success", msg: response.msg || `Hero slider ${editingHeroSlider ? 'updated' : 'created'} successfully` }));
        setHeroSliderModalOpen(false);
        resetHeroSliderForm();
        dispatch(fetchSettings());
      } else {
        dispatch(showToast({ type: "error", msg: response.msg || `Failed to ${editingHeroSlider ? 'update' : 'create'} hero slider` }));
      }
    } catch (error) {
      console.error("Error submitting hero slider:", error);
      dispatch(showToast({ type: "error", msg: error.message || `Failed to ${editingHeroSlider ? 'update' : 'create'} hero slider` }));
    } finally {
      setSubmittingHeroSlider(false);
    }
  };

  const handleDeleteHeroSliderNew = async (sliderId) => {
    if (window.confirm("Are you sure you want to delete this hero slider?")) {
      dispatch(removeHeroSlider(sliderId));
    }
  };

  // View Hero Slider Details
  const openHeroSliderViewModal = async (slider) => {
    setViewingHeroSliderLoading(true);
    setViewingHeroSlider(null);
    setHeroSliderViewModalOpen(true);

    try {
      const response = await getHeroSliderById(slider.id);
      if (!response.error && response.data) {
        setViewingHeroSlider(response.data);
      } else {
        dispatch(showToast({ type: "error", msg: response.msg || "Failed to load hero slider details" }));
        setHeroSliderViewModalOpen(false);
      }
    } catch (error) {
      console.error("Error fetching hero slider:", error);
      dispatch(showToast({ type: "error", msg: "Failed to load hero slider details" }));
      setHeroSliderViewModalOpen(false);
    } finally {
      setViewingHeroSliderLoading(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Settings" pageTitle="Settings" />
        </Container>
      </div>

      <div className="table-container mb-2 settings">
        <Container fluid>

          {loading && !settings ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-2">Loading settings...</p>
            </div>
          ) : (
            <React.Fragment>
              <Row className="mt-3">
                {/* Light Logo Upload Section */}
                <Col lg={4} md={12} className="mb-4">
                  <Card className="h-100">
                    <CardHeader className="d-flex justify-content-between align-items-center flex-wrap">
                      <h5 className="mb-0">
                        <i className="ri-sun-line me-2"></i>
                        Light Logo
                      </h5>
                    </CardHeader>
                    <CardBody className="d-flex flex-column">
                      {logoFile && logoPreview ? (
                        <div className="mb-3 flex-grow-1">
                          <Label className="fw-medium mb-2 d-block">Preview</Label>
                          <div
                            className="border rounded p-3 text-center position-relative"
                            style={{
                              backgroundColor: "#f8f9fa",
                              minHeight: "150px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer"
                            }}
                            onClick={() => openPreviewModal(logoPreview, "Light Logo Preview")}
                          >
                            <img
                              src={logoPreview}
                              alt="Light Logo Preview"
                              style={{ maxWidth: "100%", maxHeight: "120px", objectFit: "contain" }}
                            />
                            <div className="position-absolute top-0 end-0 p-2" style={{ opacity: 0.7 }}>
                              <i className="ri-eye-line fs-5 text-primary"></i>
                            </div>
                          </div>
                        </div>
                      ) : (settings?.logo && !logoFile) ? (
                        <div className="mb-3 flex-grow-1">
                          <Label className="fw-medium mb-2 d-block">Current Logo</Label>
                          <div
                            className="border rounded p-3 text-center position-relative"
                            style={{
                              backgroundColor: "#f8f9fa",
                              minHeight: "150px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer"
                            }}
                            onClick={() => openPreviewModal(getImageUrl(settings.logo), "Light Logo")}
                          >
                            <img
                              src={getImageUrl(settings.logo)}
                              alt="Light Logo"
                              style={{ maxWidth: "100%", maxHeight: "120px", objectFit: "contain" }}
                            />
                            <div className="position-absolute top-0 end-0 p-2" style={{ opacity: 0.7 }}>
                              <i className="ri-eye-line fs-5 text-primary"></i>
                            </div>
                          </div>
                        </div>
                      ) : null}

                      <div className="mt-auto">
                        <Button
                          color="primary"
                          onClick={() => openUploadModal("logo")}
                          className="w-100"
                          outline
                        >
                          <i className="ri-upload-cloud-line me-2"></i>
                          {logoFile ? "Change Logo" : "Upload Logo"}
                        </Button>
                        {logoFile && (
                          <Button
                            color="success"
                            onClick={handleUploadLogo}
                            disabled={uploadLoading}
                            className="w-100 mt-2"
                          >
                            {uploadLoading ? (
                              <>
                                <Spinner size="sm" className="me-2" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <i className="ri-check-line me-2"></i>
                                Confirm Upload
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* Dark Logo Upload Section */}
                <Col lg={4} md={12} className="mb-4">
                  <Card className="h-100">
                    <CardHeader className="d-flex justify-content-between align-items-center flex-wrap">
                      <h5 className="mb-0">
                        <i className="ri-moon-line me-2"></i>
                        Dark Logo
                      </h5>
                    </CardHeader>
                    <CardBody className="d-flex flex-column">
                      {darkLogoFile && darkLogoPreview ? (
                        <div className="mb-3 flex-grow-1">
                          <Label className="fw-medium mb-2 d-block">Preview</Label>
                          <div
                            className="border rounded p-3 text-center position-relative"
                            style={{
                              backgroundColor: "#212529",
                              minHeight: "150px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer"
                            }}
                            onClick={() => openPreviewModal(darkLogoPreview, "Dark Logo Preview")}
                          >
                            <img
                              src={darkLogoPreview}
                              alt="Dark Logo Preview"
                              style={{ maxWidth: "100%", maxHeight: "120px", objectFit: "contain" }}
                            />
                            <div className="position-absolute top-0 end-0 p-2" style={{ opacity: 0.7 }}>
                              <i className="ri-eye-line fs-5 text-light"></i>
                            </div>
                          </div>
                        </div>
                      ) : (settings?.darkLogo && !darkLogoFile) ? (
                        <div className="mb-3 flex-grow-1">
                          <Label className="fw-medium mb-2 d-block">Current Logo</Label>
                          <div
                            className="border rounded p-3 text-center position-relative"
                            style={{
                              backgroundColor: "#212529",
                              minHeight: "150px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer"
                            }}
                            onClick={() => openPreviewModal(getImageUrl(settings.darkLogo), "Dark Logo")}
                          >
                            <img
                              src={getImageUrl(settings.darkLogo)}
                              alt="Dark Logo"
                              style={{ maxWidth: "100%", maxHeight: "120px", objectFit: "contain" }}
                            />
                            <div className="position-absolute top-0 end-0 p-2" style={{ opacity: 0.7 }}>
                              <i className="ri-eye-line fs-5 text-light"></i>
                            </div>
                          </div>
                        </div>
                      ) : null}

                      <div className="mt-auto">
                        <Button
                          color="primary"
                          onClick={() => openUploadModal("darkLogo")}
                          className="w-100"
                          outline
                        >
                          <i className="ri-upload-cloud-line me-2"></i>
                          {darkLogoFile ? "Change Logo" : "Upload Logo"}
                        </Button>
                        {darkLogoFile && (
                          <Button
                            color="success"
                            onClick={handleUploadDarkLogo}
                            disabled={uploadLoading}
                            className="w-100 mt-2"
                          >
                            {uploadLoading ? (
                              <>
                                <Spinner size="sm" className="me-2" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <i className="ri-check-line me-2"></i>
                                Confirm Upload
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* Login Dialog Image Section */}
                <Col lg={4} md={12} className="mb-4">
                  <Card className="h-100">
                    <CardHeader className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">
                        <i className="ri-image-2-line me-2"></i>
                        Login Dialog Image
                      </h5>
                    </CardHeader>
                    <CardBody className="d-flex flex-column">
                      {loginDialogFile && loginDialogPreview ? (
                        <div className="mb-3 flex-grow-1">
                          <Label className="fw-medium mb-2 d-block">Preview</Label>
                          <div
                            className="border rounded p-3 text-center position-relative"
                            style={{
                              backgroundColor: "#f8f9fa",
                              minHeight: "150px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer"
                            }}
                            onClick={() => openPreviewModal(loginDialogPreview, "Login Dialog Image Preview")}
                          >
                            <img
                              src={loginDialogPreview}
                              alt="Login Dialog Preview"
                              style={{ maxWidth: "100%", maxHeight: "120px", objectFit: "contain" }}
                            />
                            <div className="position-absolute top-0 end-0 p-2" style={{ opacity: 0.7 }}>
                              <i className="ri-eye-line fs-5 text-primary"></i>
                            </div>
                          </div>
                        </div>
                      ) : (settings?.image && !loginDialogFile) ? (
                        <div className="mb-3 flex-grow-1">
                          <Label className="fw-medium mb-2 d-block">Current Image</Label>
                          <div
                            className="border rounded p-3 text-center position-relative"
                            style={{
                              backgroundColor: "#f8f9fa",
                              minHeight: "150px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer"
                            }}
                            onClick={() => openPreviewModal(getImageUrl(settings.image), "Login Dialog Image")}
                          >
                            <img
                              src={getImageUrl(settings.image)}
                              alt="Login Dialog"
                              style={{ maxWidth: "100%", maxHeight: "120px", objectFit: "contain" }}
                            />
                            <div className="position-absolute top-0 end-0 p-2" style={{ opacity: 0.7 }}>
                              <i className="ri-eye-line fs-5 text-primary"></i>
                            </div>
                          </div>
                        </div>
                      ) : null}

                      <div className="mt-auto">
                        <Button
                          color="primary"
                          onClick={() => openUploadModal("login")}
                          className="w-100"
                          outline
                        >
                          <i className="ri-upload-cloud-line me-2"></i>
                          {loginDialogFile ? "Change Image" : "Upload Image"}
                        </Button>
                        {loginDialogFile && (
                          <Button
                            color="success"
                            onClick={handleUploadLoginDialog}
                            disabled={uploadLoading}
                            className="w-100 mt-2"
                          >
                            {uploadLoading ? (
                              <>
                                <Spinner size="sm" className="me-2" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <i className="ri-check-line me-2"></i>
                                Confirm Upload
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* Hero Sliders Management Section */}
              <Row className="mt-3">
                <Col md={12} className="mb-4">
                  <Card>
                    <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <h5 className="mb-0">
                        <i className="ri-slideshow-line me-2"></i>
                        Hero Sliders Management
                      </h5>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => openHeroSliderModal()}
                      >
                        <i className="ri-add-line me-2"></i>
                        Create Hero Slider
                      </Button>
                    </CardHeader>
                    <CardBody>
                      {settings?.sliders && settings.sliders.length > 0 ? (
                        <div>
                          <Label className="fw-medium mb-3 d-block">Current Hero Sliders ({settings.sliders.length})</Label>
                          <Row>
                            {settings.sliders.map((slider, index) => {
                              const imagePath = slider.heroImage?.url || slider.url || slider.imagePath || '';
                              const imageUrl = getImageUrl(imagePath);
                              const filename = slider.heroImage?.imagePath || slider.filename || extractFilename(imagePath);
                              const isVideo = imagePath.toLowerCase().match(/\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv)$/i) || slider.type === 'video';

                              return (
                                <Col xl={3} lg={4} md={4} sm={6} xs={12} key={slider.id || index} className="mb-3">
                                  <div className="border rounded p-3 position-relative bg-white" style={{ minHeight: "200px" }}>
                                    <div
                                      style={{
                                        cursor: "pointer",
                                        position: "relative"
                                      }}
                                      onClick={() => openPreviewModal(imageUrl, slider.name || `Hero Slider ${index + 1}`, isVideo)}
                                    >
                                      {isVideo ? (
                                        <div
                                          className="position-relative bg-dark rounded"
                                          style={{ width: "100%", height: "150px", overflow: "hidden" }}
                                        >
                                          <video
                                            src={imageUrl}
                                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                            muted
                                            playsInline
                                            onMouseEnter={(e) => e.target.play()}
                                            onMouseLeave={(e) => {
                                              e.target.pause();
                                              e.target.currentTime = 0;
                                            }}
                                          />
                                          <div className="position-absolute top-50 start-50 translate-middle">
                                            <i className="ri-play-circle-fill fs-1 text-white" style={{ textShadow: "0 0 8px rgba(0,0,0,0.8)", opacity: 0.8 }}></i>
                                          </div>
                                        </div>
                                      ) : (
                                        <>
                                          <img
                                            src={imageUrl}
                                            alt={slider.name || `Hero Slider ${index + 1}`}
                                            style={{ width: "100%", height: "150px", objectFit: "cover", borderRadius: "4px" }}
                                          />
                                        </>
                                      )}
                                    </div>
                                    <div className="mt-2">
                                      <h6 className="mb-1">{slider.name || "Untitled"}</h6>
                                      {slider.price && (
                                        <p className="mb-1 text-muted small">₹{slider.price}</p>
                                      )}
                                      <div className="mb-2">
                                        <small className="text-muted d-block mb-1">Amenities:</small>
                                        {slider.amenities && slider.amenities.length > 0 ? (
                                          <div className="d-flex flex-wrap gap-1">
                                            {slider.amenities.slice(0, 3).map((amenity, idx) => {
                                              // Handle different amenity structures
                                              const amenityName = amenity?.name || amenity?.amenity?.name || (typeof amenity === 'string' ? amenity : 'Unknown');
                                              const amenityId = amenity?.id || amenity?.amenityId || idx;
                                              return (
                                                <span key={amenityId} className="badge bg-primary" style={{ fontSize: "0.7rem" }}>
                                                  {amenityName}
                                                </span>
                                              );
                                            })}
                                            {slider.amenities.length > 3 && (
                                              <span className="badge bg-secondary" style={{ fontSize: "0.7rem" }}>
                                                +{slider.amenities.length - 3}
                                              </span>
                                            )}
                                          </div>
                                        ) : (
                                          <span className="text-muted small">No amenities selected</span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="d-flex gap-2 mt-2">
                                      <Button
                                        color="info"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openHeroSliderViewModal(slider);
                                        }}
                                        disabled={loading}
                                      >
                                        <i className="ri-eye-line"></i>
                                      </Button>
                                      <Button
                                        color="primary"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openHeroSliderModal(slider);
                                        }}
                                        disabled={loading}
                                        className="flex-fill"
                                      >
                                        <i className="ri-edit-line me-1"></i>
                                        Edit
                                      </Button>
                                      <Button
                                        color="danger"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteHeroSliderNew(slider.id);
                                        }}
                                        disabled={loading}
                                      >
                                        <i className="ri-delete-bin-line"></i>
                                      </Button>
                                    </div>
                                  </div>
                                </Col>
                              );
                            })}
                          </Row>
                        </div>
                      ) : (
                        <Alert color="info" className="mb-0">
                          <i className="ri-information-line me-2"></i>
                          No hero sliders created yet. Click "Create Hero Slider" to get started.
                        </Alert>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </React.Fragment>
          )}
        </Container>
      </div>

      {/* Upload Modal */}
      <Modal isOpen={uploadModalOpen} toggle={() => setUploadModalOpen(!uploadModalOpen)} size="lg">
        <ModalHeader toggle={() => setUploadModalOpen(!uploadModalOpen)}>
          <i className={`ri-${activeUploadType === "logo" ? "sun" : activeUploadType === "darkLogo" ? "moon" : activeUploadType === "login" ? "image-2" : "slideshow"}-line me-2`}></i>
          Upload {activeUploadType === "logo" ? "Light Logo" : activeUploadType === "darkLogo" ? "Dark Logo" : activeUploadType === "login" ? "Login Dialog Image" : "Hero Slider Images"}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Label className="fw-medium mb-2">
              Select {activeUploadType === "hero" ? "Images (Multiple)" : "Image"}
            </Label>
            <Input
              type="file"
              accept={activeUploadType === "hero" ? "image/*,video/*" : "image/*"}
              multiple={activeUploadType === "hero"}
              onChange={(e) => handleFileSelect(e, activeUploadType)}
              innerRef={activeUploadType === "logo" ? logoInputRef : activeUploadType === "darkLogo" ? darkLogoInputRef : activeUploadType === "login" ? loginDialogInputRef : heroSliderInputRef}
              className="mt-2"
            />
            <small className="text-muted d-block mt-2">
              <i className="ri-information-line me-1"></i>
              {activeUploadType === "logo"
                ? "Light mode logo. Recommended size: 200x100px"
                : activeUploadType === "darkLogo"
                  ? "Dark mode logo. Recommended size: 200x100px"
                  : activeUploadType === "login"
                    ? "Recommended size: 1920x1080px for best display"
                    : "You can select multiple images/videos at once. Recommended size: 1920x1080px"}
            </small>
          </div>

          {activeUploadType === "logo" && logoFile && logoPreview && (
            <div className="mt-3">
              <Label className="fw-medium mb-2">Preview</Label>
              <div className="border rounded p-3 text-center" style={{ backgroundColor: "#f8f9fa" }}>
                <img
                  src={logoPreview}
                  alt="Logo Preview"
                  style={{ maxWidth: "100%", maxHeight: "200px", objectFit: "contain" }}
                />
              </div>
            </div>
          )}

          {activeUploadType === "darkLogo" && darkLogoFile && darkLogoPreview && (
            <div className="mt-3">
              <Label className="fw-medium mb-2">Preview</Label>
              <div className="border rounded p-3 text-center" style={{ backgroundColor: "#212529" }}>
                <img
                  src={darkLogoPreview}
                  alt="Dark Logo Preview"
                  style={{ maxWidth: "100%", maxHeight: "200px", objectFit: "contain" }}
                />
              </div>
            </div>
          )}

          {activeUploadType === "login" && loginDialogFile && loginDialogPreview && (
            <div className="mt-3">
              <Label className="fw-medium mb-2">Preview</Label>
              <div className="border rounded p-3 text-center" style={{ backgroundColor: "#f8f9fa" }}>
                <img
                  src={loginDialogPreview}
                  alt="Login Dialog Preview"
                  style={{ maxWidth: "100%", maxHeight: "300px", objectFit: "contain" }}
                />
              </div>
            </div>
          )}

          {activeUploadType === "hero" && heroSliderFiles.length > 0 && (
            <div className="mt-3">
              <Label className="fw-medium mb-2">Previews ({heroSliderFiles.length})</Label>
              <Row>
                {heroSliderFiles.map((file, index) => (
                  <Col md={3} key={index} className="mb-3">
                    <div className="border rounded p-2">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Preview ${index + 1}`}
                        style={{ width: "100%", height: "120px", objectFit: "cover", borderRadius: "4px" }}
                      />
                      <small className="d-block mt-2 text-truncate" title={file.name}>
                        {file.name}
                      </small>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setUploadModalOpen(false)}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleUploadFromModal}
            disabled={uploadLoading || (activeUploadType === "logo" && !logoFile) || (activeUploadType === "darkLogo" && !darkLogoFile) || (activeUploadType === "login" && !loginDialogFile) || (activeUploadType === "hero" && heroSliderFiles.length === 0)}
          >
            {uploadLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Uploading...
              </>
            ) : (
              <>
                <i className="ri-upload-cloud-line me-2"></i>
                Upload
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Preview Modal */}
      <Modal isOpen={previewModalOpen} toggle={() => setPreviewModalOpen(!previewModalOpen)} size="xl">
        <ModalHeader toggle={() => setPreviewModalOpen(!previewModalOpen)}>
          <i className={previewIsVideo ? "ri-video-line me-2" : "ri-image-line me-2"}></i>
          {previewTitle}
        </ModalHeader>
        <ModalBody className="text-center" style={{ minHeight: "400px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#000" }}>
          {previewImage && (
            previewIsVideo ? (
              <video
                src={previewImage}
                controls
                autoPlay
                style={{ maxWidth: "100%", maxHeight: "80vh", width: "100%" }}
              >
                Your browser does not support the video tag.
              </video>
            ) : (
              <img
                src={previewImage}
                alt={previewTitle}
                style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
              />
            )
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setPreviewModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Hero Slider Create/Edit Modal */}
      <Modal isOpen={heroSliderModalOpen} toggle={() => {
        setHeroSliderModalOpen(false);
        resetHeroSliderForm();
      }} size="xl" centered>
        <ModalHeader toggle={() => {
          setHeroSliderModalOpen(false);
          resetHeroSliderForm();
        }}>
          <i className="ri-slideshow-line me-2"></i>
          {editingHeroSlider ? "Edit Hero Slider" : "Create Hero Slider"}
        </ModalHeader>
        <ModalBody style={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}>
          {/* Image/Video Upload Section */}
          <div className="mb-4">
            <Label className="fw-medium mb-2">
              Hero Image/Video {!editingHeroSlider && <span className="text-danger">*</span>}
            </Label>
            {heroSliderPreview ? (
              <div className="position-relative border rounded p-3" style={{ backgroundColor: "#f8f9fa" }}>
                {heroSliderIsVideo ? (
                  <video
                    src={heroSliderPreview}
                    controls
                    style={{ width: "100%", maxHeight: "300px", borderRadius: "4px" }}
                  />
                ) : (
                  <img
                    src={heroSliderPreview}
                    alt="Preview"
                    style={{ width: "100%", maxHeight: "300px", objectFit: "contain", borderRadius: "4px" }}
                  />
                )}
                <Button
                  color="danger"
                  size="sm"
                  className="position-absolute top-0 end-0 m-2"
                  onClick={handleCancelHeroSliderFile}
                >
                  <i className="ri-close-line"></i> Cancel
                </Button>
              </div>
            ) : (
              <div>
                <Input
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleHeroSliderFileChange}
                  innerRef={heroSliderFileInputRef}
                />
                <small className="text-muted d-block mt-2">
                  <i className="ri-information-line me-1"></i>
                  Upload an image or video (max 50MB). Supported: jpg, png, gif, webp, mp4, mov, avi, wmv, flv, webm, mkv
                </small>
              </div>
            )}
          </div>

          {/* Farmhouse Selection */}
          <div className="mb-3">
            <Label className="fw-medium mb-2">
              Farmhouse <span className="text-danger">*</span>
            </Label>
            <Select
              isClearable
              isSearchable
              options={farmhouses.map(f => ({
                value: f.id,
                label: `${f.name}${f.farmNo ? ` (${f.farmNo})` : ''}`
              }))}
              value={selectedFarmhouse}
              onChange={handleFarmhouseChange}
              placeholder="Select a farmhouse"
              isLoading={farmhousesLoading}
              classNamePrefix="react-select"
            />
          </div>

          {/* Auto-filled Farm Information */}
          {selectedFarmhouse && (
            <Row className="mb-3">
              <Col md={6}>
                <Label className="fw-medium mb-2">Price</Label>
                <Input
                  type="number"
                  value={farmPrice || ""}
                  onChange={(e) => setFarmPrice(e.target.value ? parseFloat(e.target.value) : null)}
                  placeholder="Enter price"
                  min="0"
                  step="0.01"
                />
                <small className="text-muted d-block mt-1">
                  <i className="ri-information-line me-1"></i>
                  Fetched: 24 Hour Weekend Price (₹{farmPrice || "N/A"})
                </small>
              </Col>
              <Col md={6}>
                <Label className="fw-medium mb-2">Near By</Label>
                <Input
                  type="text"
                  value={farmNearby || "N/A"}
                  disabled
                  className="bg-light"
                />
              </Col>
            </Row>
          )}

          {/* Amenities Selection */}
          {selectedFarmhouse && (
            <div className="mb-3">
              <Label className="fw-medium mb-2">
                Select Amenities <span className="text-danger">*</span>
                <small className="text-muted ms-2">(Min: 1, Max: 4)</small>
              </Label>

              {amenitiesLoading ? (
                <div className="text-center py-3">
                  <Spinner size="sm" className="me-2" />
                  Loading amenities...
                </div>
              ) : availableAmenities.length > 0 ? (
                <div>
                  {selectedAmenities.length >= 4 ? (
                    // Show only selected 4 amenities when 4 are selected
                    <div>
                      <div className="border rounded p-3">
                        <Row>
                          {selectedAmenities.map((amenity) => {
                            const amenityId = amenity?.id || amenity?.amenityId;
                            const amenityName = amenity?.name || amenity?.amenity?.name || 'Unknown';
                            if (!amenityId) return null;

                            const fullAmenity = availableAmenities.find(a => {
                              const aId = a?.id || a?.amenityId;
                              return aId === amenityId;
                            });

                            return (
                              <Col md={6} key={amenityId} className="mb-2">
                                <div className="form-check">
                                  <Input
                                    type="checkbox"
                                    id={`amenity-selected-${amenityId}`}
                                    className="form-check-input"
                                    checked={true}
                                    onChange={() => toggleAmenity(fullAmenity || amenity)}
                                  />
                                  <Label htmlFor={`amenity-selected-${amenityId}`} className="form-check-label">
                                    {amenityName}
                                    {fullAmenity?.category && (
                                      <small className="text-muted ms-2">({fullAmenity.category})</small>
                                    )}
                                  </Label>
                                </div>
                              </Col>
                            );
                          })}
                        </Row>
                      </div>
                    </div>
                  ) : (
                    // Show all amenities grouped by category when less than 4 selected
                    (() => {
                      const groupedAmenities = {};
                      availableAmenities.forEach(amenity => {
                        const category = amenity.category || "Other";
                        if (!groupedAmenities[category]) {
                          groupedAmenities[category] = [];
                        }
                        groupedAmenities[category].push(amenity);
                      });

                      return Object.entries(groupedAmenities).map(([category, items]) => (
                        <div key={category} className="mb-3">
                          <h6 className="fw-semibold mb-2">{category}</h6>
                          <div className="border rounded p-3">
                            <Row>
                              {items.map((amenity) => {
                                const amenityId = amenity?.id || amenity?.amenityId;
                                const amenityName = amenity?.name || amenity?.amenity?.name || 'Unknown';
                                if (!amenityId) return null;

                                const isSelected = selectedAmenities.some(a => {
                                  const aId = a?.id || a?.amenityId;
                                  return aId === amenityId;
                                });

                                return (
                                  <Col md={6} key={amenityId} className="mb-2">
                                    <div className="form-check">
                                      <Input
                                        type="checkbox"
                                        id={`amenity-${amenityId}`}
                                        className="form-check-input"
                                        checked={isSelected}
                                        onChange={() => toggleAmenity(amenity)}
                                        disabled={!isSelected && selectedAmenities.length >= 4}
                                      />
                                      <Label htmlFor={`amenity-${amenityId}`} className="form-check-label">
                                        {amenityName}
                                      </Label>
                                    </div>
                                  </Col>
                                );
                              })}
                            </Row>
                          </div>
                        </div>
                      ));
                    })()
                  )}

                  {selectedAmenities.length > 0 && (
                    <div className="mt-3">
                      <Label className="fw-medium mb-2 d-flex justify-content-between align-items-center">
                        <span>Selected Amenities ({selectedAmenities.length}/4)</span>
                        {selectedAmenities.length > 0 && (
                          <Button
                            color="link"
                            size="sm"
                            className="text-danger p-0"
                            onClick={() => {
                              setSelectedAmenities([]);
                            }}
                            style={{ fontSize: "0.75rem", textDecoration: "none" }}
                          >
                            <i className="ri-close-circle-line me-1"></i>
                            Clear All
                          </Button>
                        )}
                      </Label>
                      <div className="border rounded p-3 bg-light">
                        <div className="d-flex flex-wrap gap-2">
                          {selectedAmenities.map((amenity) => {
                            // Ensure we have a valid amenity object with name
                            const amenityName = amenity?.name || amenity?.amenity?.name || 'Unknown';
                            const amenityId = amenity?.id || amenity?.amenityId;
                            if (!amenityId) return null;

                            return (
                              <span
                                key={amenityId}
                                className="badge bg-primary d-flex align-items-center gap-1"
                                style={{ fontSize: "0.85rem", padding: "0.4rem 0.6rem" }}
                              >
                                <span>{amenityName}</span>
                                <button
                                  type="button"
                                  className="btn-close btn-close-white"
                                  style={{ fontSize: "0.5rem", margin: 0 }}
                                  onClick={() => {
                                    const fullAmenity = availableAmenities.find(a => a.id === amenityId);
                                    toggleAmenity(fullAmenity || amenity);
                                  }}
                                  aria-label="Remove"
                                  title="Remove amenity"
                                ></button>
                              </span>
                            );
                          })}
                        </div>
                        {selectedAmenities.length > 0 && (
                          <small className="text-muted d-block mt-2">
                            <i className="ri-information-line me-1"></i>
                            Click the X button on any badge to remove that amenity
                          </small>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Alert color="warning">
                  <i className="ri-alert-line me-2"></i>
                  No amenities available for this farmhouse.
                </Alert>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              setHeroSliderModalOpen(false);
              resetHeroSliderForm();
            }}
            disabled={submittingHeroSlider}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSubmitHeroSlider}
            disabled={submittingHeroSlider || !selectedFarmhouse || selectedAmenities.length < 1 || (!editingHeroSlider && !heroSliderFile)}
          >
            {submittingHeroSlider ? (
              <>
                <Spinner size="sm" className="me-2" />
                {editingHeroSlider ? "Updating..." : "Creating..."}
              </>
            ) : (
              <>
                <i className={editingHeroSlider ? "ri-save-line me-2" : "ri-add-line me-2"}></i>
                {editingHeroSlider ? "Update" : "Create"}
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Hero Slider View Modal */}
      <Modal isOpen={heroSliderViewModalOpen} toggle={() => setHeroSliderViewModalOpen(false)} size="lg" centered>
        <ModalHeader toggle={() => setHeroSliderViewModalOpen(false)}>
          <i className="ri-slideshow-line me-2"></i>
          {viewingHeroSlider?.name || "Hero Slider Details"}
        </ModalHeader>
        <ModalBody style={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}>
          {viewingHeroSliderLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-2">Loading hero slider details...</p>
            </div>
          ) : viewingHeroSlider ? (
            <div>
              {/* Hero Image/Video */}
              {viewingHeroSlider.heroImage && (
                <div className="mb-4">
                  <Label className="fw-medium mb-2">Hero Image/Video</Label>
                  <div className="border rounded p-3 text-center" style={{ backgroundColor: "#f8f9fa" }}>
                    {(() => {
                      const imagePath = viewingHeroSlider.heroImage.url || viewingHeroSlider.heroImage.imagePath || '';
                      const imageUrl = getImageUrl(imagePath);
                      const isVideo = imagePath.toLowerCase().match(/\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv)$/i);

                      return isVideo ? (
                        <video
                          src={imageUrl}
                          controls
                          style={{ width: "100%", maxHeight: "400px", borderRadius: "4px" }}
                        />
                      ) : (
                        <img
                          src={imageUrl}
                          alt={viewingHeroSlider.name}
                          style={{ width: "100%", maxHeight: "400px", objectFit: "contain", borderRadius: "4px" }}
                        />
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Farmhouse Information */}
              <Row className="mb-3">
                <Col md={6}>
                  <Label className="fw-medium mb-2">Farmhouse Name</Label>
                  <Input
                    type="text"
                    value={viewingHeroSlider.name || "N/A"}
                    disabled
                    className="bg-light"
                  />
                </Col>
                <Col md={6}>
                  <Label className="fw-medium mb-2">Price</Label>
                  <Input
                    type="text"
                    value={viewingHeroSlider.price ? `₹${viewingHeroSlider.price}` : "N/A"}
                    disabled
                    className="bg-light"
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Label className="fw-medium mb-2">Near By</Label>
                  <Input
                    type="text"
                    value={viewingHeroSlider.nearby || "N/A"}
                    disabled
                    className="bg-light"
                  />
                </Col>
                <Col md={6}>
                  <Label className="fw-medium mb-2">Farmhouse ID</Label>
                  <Input
                    type="text"
                    value={viewingHeroSlider.farmId || "N/A"}
                    disabled
                    className="bg-light"
                  />
                </Col>
              </Row>

              {/* Amenities - Grouped by Category */}
              <div className="mb-3">
                <Label className="fw-medium mb-2">
                  Selected Amenities ({viewingHeroSlider.amenities?.length || 0}/4)
                </Label>
                {viewingHeroSlider.amenities && viewingHeroSlider.amenities.length > 0 ? (
                  (() => {
                    // Group amenities by category
                    const groupedAmenities = {};
                    viewingHeroSlider.amenities.forEach((item) => {
                      const categoryName = item.category?.name || item.category || "Other";
                      const amenityName = item.amenity?.name || item.name || "Unknown";

                      if (!groupedAmenities[categoryName]) {
                        groupedAmenities[categoryName] = [];
                      }
                      groupedAmenities[categoryName].push({
                        id: item.amenityId || item.amenity?.id || item.id,
                        name: amenityName,
                        quantity: item.quantity || 1
                      });
                    });

                    return Object.entries(groupedAmenities).map(([category, items]) => (
                      <div key={category} className="mb-3">
                        <h6 className="fw-semibold mb-2">
                          <i className="ri-folder-line me-2"></i>
                          {category}
                        </h6>
                        <div className="border rounded p-3 bg-light">
                          <Row>
                            {items.map((amenity, idx) => (
                              <Col md={6} key={amenity.id || idx} className="mb-2">
                                <div className="d-flex align-items-center">
                                  <i className="ri-checkbox-circle-fill text-success me-2"></i>
                                  <span>{amenity.name}</span>
                                  {amenity.quantity > 1 && (
                                    <span className="badge bg-secondary ms-2" style={{ fontSize: "0.7rem" }}>
                                      x{amenity.quantity}
                                    </span>
                                  )}
                                </div>
                              </Col>
                            ))}
                          </Row>
                        </div>
                      </div>
                    ));
                  })()
                ) : (
                  <Alert color="info" className="mb-0">
                    <i className="ri-information-line me-2"></i>
                    No amenities selected for this hero slider.
                  </Alert>
                )}
              </div>
            </div>
          ) : (
            <Alert color="warning">
              <i className="ri-alert-line me-2"></i>
              Failed to load hero slider details.
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setHeroSliderViewModalOpen(false)}>
            Close
          </Button>
          {viewingHeroSlider && (
            <Button
              color="primary"
              onClick={() => {
                setHeroSliderViewModalOpen(false);
                openHeroSliderModal(viewingHeroSlider);
              }}
            >
              <i className="ri-edit-line me-2"></i>
              Edit
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default Settings;

